import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Key, Plus, X, RefreshCw, Check, Trash2, Copy, Eye, EyeOff,
  ToggleLeft, ToggleRight, BookOpen, Code, ExternalLink, Database,
  Search, Filter, Globe, Lock, Unlock, ChevronDown, ChevronUp,
  Zap, AlertCircle, Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_COLORS = {
  GET: "bg-green-500/20 text-green-300 border-green-500/30",
  POST: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  PUT: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  DELETE: "bg-red-500/20 text-red-300 border-red-500/30",
  PATCH: "bg-purple-500/20 text-purple-300 border-purple-500/30",
};

const TAG_COLORS = {
  auth: "text-blue-400", admin: "text-red-400", chat: "text-green-400",
  agents: "text-purple-400", "ai-agents": "text-purple-400",
  "knowledge-apis": "text-yellow-400", webhooks: "text-orange-400",
  oauth: "text-pink-400", integrations: "text-cyan-400",
  general: "text-gray-400",
};

// ─── Create API Key Modal ─────────────────────────────────────────────────────
function CreateKeyModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(["read"]);
  const [saving, setSaving] = useState(false);

  const PERMS = [
    { id: "read", label: "Lectura", desc: "GET endpoints" },
    { id: "write", label: "Escritura", desc: "POST/PUT endpoints" },
    { id: "delete", label: "Eliminación", desc: "DELETE endpoints" },
    { id: "admin", label: "Admin", desc: "Endpoints administrativos" },
  ];

  const togglePerm = (p) => {
    if (permissions.includes(p)) setPermissions(permissions.filter(x => x !== p));
    else setPermissions([...permissions, p]);
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("El nombre es requerido"); return; }
    if (permissions.length === 0) { toast.error("Selecciona al menos un permiso"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api-keys`, { name: name.trim(), permissions });
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear API Key");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-md animate-scale-in">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <div>
            <h3 className="text-white font-semibold text-lg">Nueva API Key</h3>
            <p className="text-gray-500 text-xs mt-0.5">Para acceso externo seguro a la API de OkBot</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre de la Key *</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Ej: Bubble.io Production, App Mobile..."
              className="input-glass w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-2 font-medium">Permisos</label>
            <div className="grid grid-cols-2 gap-2">
              {PERMS.map(p => (
                <label
                  key={p.id}
                  className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                    permissions.includes(p.id)
                      ? "bg-red-500/15 border-red-500/40"
                      : "bg-white/3 border-white/5 hover:bg-white/8"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(p.id)}
                    onChange={() => togglePerm(p.id)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    permissions.includes(p.id) ? "bg-red-500 border-red-500" : "border-white/20"
                  }`}>
                    {permissions.includes(p.id) && <Check size={10} className="text-white" />}
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">{p.label}</p>
                    <p className="text-gray-500 text-xs">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-xs text-gray-400">
            <AlertCircle size={12} className="text-yellow-400 inline mr-1.5" />
            La clave secreta completa se mostrará <strong className="text-white">una sola vez</strong> al crear. Guárdala de inmediato.
          </div>
        </div>

        <div className="flex gap-3 p-6 pt-0">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={handleCreate} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Key size={14} />}
            Generar API Key
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── API Key Card ─────────────────────────────────────────────────────────────
function ApiKeyCard({ k, onDelete, onToggle, createdKey }) {
  const copyPrefix = () => { navigator.clipboard.writeText(k.key_prefix || ""); toast.success("Prefijo copiado"); };
  const copyFullKey = () => { navigator.clipboard.writeText(createdKey); toast.success("API Key copiada"); };

  const permBadges = {
    read: "badge-success", write: "badge-primary",
    delete: "badge-error", admin: "bg-purple-500/20 text-purple-300 border border-purple-500/30",
  };

  return (
    <div className={`glass-panel p-4 ${!k.is_active ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
            k.is_active ? "bg-red-500/20" : "bg-gray-500/20"
          }`}>
            {k.is_active ? <Key size={16} className="text-red-400" /> : <Lock size={16} className="text-gray-500" />}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium">{k.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <code className="text-gray-500 text-xs font-mono">{k.key_prefix}</code>
              <button onClick={copyPrefix} className="text-gray-600 hover:text-gray-400">
                <Copy size={10} />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
          <span className={`badge text-xs ${k.is_active ? "badge-success" : "badge-error"}`}>
            {k.is_active ? "Activa" : "Inactiva"}
          </span>
          <button onClick={() => onToggle(k.id)} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg" title="Activar/Desactivar">
            {k.is_active ? <ToggleRight size={16} className="text-green-400" /> : <ToggleLeft size={16} />}
          </button>
          <button onClick={() => onDelete(k.id)} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg" title="Eliminar">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1">
          {(k.permissions || []).map(p => (
            <span key={p} className={`badge text-xs ${permBadges[p] || "badge-primary"}`}>{p}</span>
          ))}
        </div>
        <span className="text-gray-600 text-xs">{k.created_at ? new Date(k.created_at).toLocaleDateString() : ""}</span>
      </div>

      {/* Show full key right after creation */}
      {createdKey && (
        <div className="mt-3 bg-green-500/10 border border-green-500/20 rounded-xl p-3">
          <p className="text-green-400 text-xs mb-2 flex items-center gap-1">
            <AlertCircle size={11} />Nueva clave — guárdala ahora, no se volverá a mostrar
          </p>
          <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2">
            <code className="text-green-300 text-xs font-mono flex-1 break-all">{createdKey}</code>
            <button onClick={copyFullKey} className="p-1 text-gray-400 hover:text-white flex-shrink-0">
              <Copy size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Endpoints Catalog ────────────────────────────────────────────────────────
function EndpointsCatalog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("all");
  const [expandedTags, setExpandedTags] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/system-endpoints`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error("Error al cargar endpoints"); setLoading(false); });
  }, []);

  const copyEndpoint = (ep) => {
    navigator.clipboard.writeText(ep.full_url);
    toast.success("URL copiada");
  };

  const copyPath = (path) => {
    navigator.clipboard.writeText(path);
    toast.success("Path copiado");
  };

  if (loading) return <div className="flex items-center justify-center py-16"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>;
  if (!data) return null;

  const tags = ["all", ...Object.keys(data.grouped || {}).sort()];
  const filtered = (data.endpoints || []).filter(ep => {
    const matchTag = activeTag === "all" || ep.tags.includes(activeTag);
    const matchSearch = !search ||
      ep.path.toLowerCase().includes(search.toLowerCase()) ||
      ep.summary.toLowerCase().includes(search.toLowerCase()) ||
      ep.method.toLowerCase().includes(search.toLowerCase());
    return matchTag && matchSearch;
  });

  const grouped = {};
  filtered.forEach(ep => {
    const tag = ep.tags[0] || "general";
    if (!grouped[tag]) grouped[tag] = [];
    grouped[tag].push(ep);
  });

  return (
    <div className="space-y-4">
      {/* Stats + Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar endpoints por path, método o descripción..."
            className="input-glass w-full pl-9 text-sm"
          />
        </div>
        <div className="bg-black/30 rounded-xl px-3 py-2 text-xs text-gray-400 whitespace-nowrap">
          <span className="text-white font-medium">{filtered.length}</span> / {data.total} endpoints
        </div>
      </div>

      {/* Tag filters */}
      <div className="flex flex-wrap gap-1.5">
        {tags.map(tag => (
          <button
            key={tag}
            onClick={() => setActiveTag(tag)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
              activeTag === tag
                ? "bg-red-500/20 text-red-300 border border-red-500/30"
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
          >
            {tag === "all" ? "Todos" : tag}
            {tag !== "all" && (
              <span className="ml-1 text-gray-600">
                ({(data.grouped[tag] || []).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Grouped Endpoints */}
      <div className="space-y-3">
        {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([tag, eps]) => (
          <div key={tag} className="glass-panel overflow-hidden">
            <button
              onClick={() => setExpandedTags(t => ({ ...t, [tag]: !t[tag] }))}
              className="w-full flex items-center justify-between p-4 hover:bg-white/3 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <span className={`text-sm font-medium ${TAG_COLORS[tag] || "text-gray-400"}`}>{tag}</span>
                <span className="text-gray-600 text-xs">{eps.length} endpoint{eps.length !== 1 ? "s" : ""}</span>
              </div>
              {expandedTags[tag] ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
            </button>

            {(expandedTags[tag] || search) && (
              <div className="border-t border-white/5 divide-y divide-white/3">
                {eps.map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 hover:bg-white/3 transition-colors group">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded border font-mono flex-shrink-0 ${
                      METHOD_COLORS[ep.method] || "bg-gray-500/20 text-gray-300 border-gray-500/30"
                    }`}>
                      {ep.method}
                    </span>
                    <code className="text-gray-300 text-xs flex-1 min-w-0 truncate font-mono">{ep.path}</code>
                    <p className="text-gray-600 text-xs flex-1 min-w-0 truncate hidden md:block">{ep.summary}</p>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => copyPath(ep.path)} className="p-1 text-gray-500 hover:text-gray-300 hover:bg-white/10 rounded" title="Copiar path">
                        <Copy size={11} />
                      </button>
                      <button onClick={() => copyEndpoint(ep)} className="p-1 text-gray-500 hover:text-gray-300 hover:bg-white/10 rounded" title="Copiar URL completa">
                        <Globe size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Callback Secret ──────────────────────────────────────────────────────────
function CallbackSecretPanel() {
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newSecret, setNewSecret] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/callback-secret/status`);
      setConfigured(Boolean(r.data?.configured));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo consultar el estado del secret");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const generate = async () => {
    const message = configured
      ? "Ya existe un OKBOT_CALLBACK_SECRET. Generar otro reemplazará el valor anterior cuando guardes el nuevo en Replit Secrets. ¿Continuar?"
      : "Se generará un valor nuevo. Deberás copiarlo y guardarlo en Replit Secrets. ¿Continuar?";
    if (!window.confirm(message)) return;

    setGenerating(true);
    setNewSecret("");
    try {
      const r = await axios.post(`${API}/admin/callback-secret/generate`);
      setNewSecret(r.data.secret);
      toast.success("Secret generado. Cópialo y guárdalo en Replit Secrets.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el secret");
    } finally {
      setGenerating(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(newSecret);
      toast.success("Secret copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar automáticamente");
    }
  };

  return (
    <div className="glass-panel p-6 space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
          <Lock size={19} className="text-orange-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">OKBOT_CALLBACK_SECRET</h3>
          <p className="text-gray-500 text-sm mt-1">
            Secret usado para validar callbacks y webhooks internos de OkBot.
          </p>
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${
        loading
          ? "bg-white/5 border-white/10"
          : configured
            ? "bg-green-500/10 border-green-500/25"
            : "bg-yellow-500/10 border-yellow-500/25"
      }`}>
        <div className="flex items-center gap-2">
          {loading ? <RefreshCw size={14} className="animate-spin text-gray-400" /> :
            configured ? <Check size={15} className="text-green-400" /> :
              <AlertCircle size={15} className="text-yellow-400" />}
          <span className={`text-sm font-medium ${
            loading ? "text-gray-400" : configured ? "text-green-300" : "text-yellow-300"
          }`}>
            {loading ? "Consultando estado..." : configured ? "Configurado en el entorno" : "Aún no configurado"}
          </span>
        </div>
        {!loading && (
          <p className="text-gray-500 text-xs mt-2">
            Por seguridad, el valor existente nunca se muestra.
          </p>
        )}
      </div>

      {newSecret && (
        <div className="bg-green-500/10 border border-green-500/25 rounded-xl p-4">
          <p className="text-green-300 text-xs font-medium flex items-center gap-1.5 mb-2">
            <AlertCircle size={13} />Nuevo valor — cópialo ahora, no se volverá a mostrar
          </p>
          <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2.5">
            <code className="text-green-200 text-xs font-mono flex-1 break-all select-all">{newSecret}</code>
            <button onClick={copySecret} className="btn-secondary flex items-center gap-1.5 text-xs py-1.5 px-2.5 flex-shrink-0">
              <Copy size={13} />Copiar
            </button>
          </div>
          <p className="text-yellow-300/80 text-xs mt-3">
            En Replit abre <strong>Secrets</strong>, crea o actualiza
            <code className="mx-1 text-yellow-200">OKBOT_CALLBACK_SECRET</code>
            y pega este valor. No lo guardes en el código ni lo compartas.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-gray-600 text-xs max-w-lg">
          Generar un valor nuevo no cambia Replit Secrets automáticamente. Debes copiarlo y guardarlo manualmente.
        </p>
        <button onClick={generate} disabled={generating} className="btn-primary flex items-center gap-2">
          {generating ? <RefreshCw size={14} className="animate-spin" /> : <Key size={14} />}
          {configured ? "Regenerar secret" : "Generar secret"}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function APIKeysDocsManager() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState(null);
  const [activeTab, setActiveTab] = useState("keys");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api-keys`);
      setApiKeys(r.data || []);
    } catch {
      toast.error("Error al cargar API Keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (data) => {
    setShowCreate(false);
    setNewlyCreated(data);
    setApiKeys(keys => [{ ...data, key_prefix: data.key?.slice(0, 15) + "..." }, ...keys]);
    toast.success("API Key creada — guárdala ahora");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta API Key? Las integraciones que la usen dejarán de funcionar.")) return;
    try {
      await axios.delete(`${API}/api-keys/${id}`);
      toast.success("API Key eliminada");
      setApiKeys(keys => keys.filter(k => k.id !== id));
      if (newlyCreated?.id === id) setNewlyCreated(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleToggle = async (id) => {
    try {
      const r = await axios.put(`${API}/api-keys/${id}/toggle`);
      setApiKeys(keys => keys.map(k => k.id === id ? { ...k, is_active: r.data.is_active } : k));
      toast.success(`API Key ${r.data.is_active ? "activada" : "desactivada"}`);
    } catch {
      toast.error("Error al cambiar estado");
    }
  };

  const tabs = [
    { id: "keys", label: "API Keys", icon: <Key size={14} /> },
    { id: "callback-secret", label: "Callback Secret", icon: <Lock size={14} /> },
    { id: "endpoints", label: "Catálogo de Endpoints", icon: <Code size={14} /> },
    { id: "docs", label: "Documentación", icon: <BookOpen size={14} /> },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Key size={22} className="text-red-400" />API Keys & Documentación
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona acceso externo y explora todos los endpoints del sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === t.id
                ? "bg-red-500/20 text-red-400 border border-red-500/30"
                : "text-gray-400 hover:bg-white/5"
            }`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* API Keys Tab */}
      {activeTab === "keys" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">
                <span className="text-white font-medium">{apiKeys.filter(k => k.is_active).length}</span> activas de {apiKeys.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
              <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
                <Plus size={16} />Nueva API Key
              </button>
            </div>
          </div>

          {/* Usage info */}
          <div className="glass-panel p-4 mb-4 bg-blue-500/5 border-blue-500/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-gray-400 font-medium mb-1">Header de autenticación</p>
                <code className="text-blue-300">X-API-Key: sk-okbot-xxxxxxxx</code>
              </div>
              <div>
                <p className="text-gray-400 font-medium mb-1">Base URL</p>
                <code className="text-blue-300">{BACKEND_URL}/api</code>
              </div>
              <div>
                <p className="text-gray-400 font-medium mb-1">Alternativa Bearer</p>
                <code className="text-blue-300">Authorization: Bearer &lt;key&gt;</code>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>
          ) : apiKeys.length === 0 ? (
            <div className="glass-panel p-12 text-center">
              <Key size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 font-medium mb-2">No hay API Keys generadas</p>
              <p className="text-gray-600 text-sm mb-6">Crea una API Key para conectar plataformas externas como Bubble.io</p>
              <button onClick={() => setShowCreate(true)} className="btn-primary mx-auto flex items-center gap-2">
                <Plus size={16} />Generar Primera Key
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map(k => (
                <ApiKeyCard
                  key={k.id}
                  k={k}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  createdKey={newlyCreated?.id === k.id ? newlyCreated.key : null}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "callback-secret" && <CallbackSecretPanel />}

      {/* Endpoints Catalog Tab */}
      {activeTab === "endpoints" && (
        <EndpointsCatalog />
      )}

      {/* Docs Tab */}
      {activeTab === "docs" && (
        <div className="space-y-6">
          {/* Quick links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "Swagger UI", desc: "Explorador interactivo de la API", url: `${BACKEND_URL}/api/docs`, icon: Zap, color: "text-green-400", bg: "bg-green-500/10" },
              { label: "ReDoc", desc: "Documentación limpia y navegable", url: `${BACKEND_URL}/api/redoc`, icon: BookOpen, color: "text-blue-400", bg: "bg-blue-500/10" },
              { label: "OpenAPI JSON", desc: "Esquema completo para importar", url: `${BACKEND_URL}/api/openapi.json`, icon: Database, color: "text-purple-400", bg: "bg-purple-500/10" },
            ].map((d) => {
              const Icon = d.icon;
              return (
                <a
                  key={d.label}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`glass-panel p-5 flex items-start gap-4 hover:bg-white/5 transition-colors group ${d.bg} border-opacity-20`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${d.bg}`}>
                    <Icon size={20} className={d.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium">{d.label}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{d.desc}</p>
                  </div>
                  <ExternalLink size={14} className="text-gray-600 group-hover:text-gray-400 transition-colors flex-shrink-0 mt-1" />
                </a>
              );
            })}
          </div>

          {/* Auth guide */}
          <div className="glass-panel p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Shield size={18} className="text-red-400" />Guía de Autenticación
            </h3>
            <div className="space-y-4">
              <div className="bg-black/30 rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-2 font-medium">Opción 1: API Key en header</p>
                <pre className="text-green-300 text-xs font-mono overflow-x-auto">{`curl -H "X-API-Key: sk-okbot-xxxx" \\
  ${BACKEND_URL}/api/admin/users`}</pre>
              </div>
              <div className="bg-black/30 rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-2 font-medium">Opción 2: Bearer Token (JWT)</p>
                <pre className="text-green-300 text-xs font-mono overflow-x-auto">{`curl -H "Authorization: Bearer <jwt_token>" \\
  ${BACKEND_URL}/api/chat/send`}</pre>
              </div>
              <div className="bg-black/30 rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-2 font-medium">Rate Limits</p>
                <div className="space-y-1 text-xs text-gray-400">
                  <p><span className="text-white">General:</span> 1,000 requests / minuto</p>
                  <p><span className="text-white">IA / Chat:</span> 100 requests / minuto</p>
                  <p><span className="text-white">Webhooks:</span> Sin límite para recepción</p>
                </div>
              </div>
            </div>
          </div>

          {/* Webhooks guide */}
          <div className="glass-panel p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Code size={18} className="text-red-400" />Eventos Webhook
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                "user.created", "user.updated", "user.deleted",
                "business.created", "order.created", "order.updated",
                "payment.completed", "payment.failed", "agent.message",
              ].map(ev => (
                <div key={ev} className="flex items-center gap-2 bg-black/20 rounded-lg p-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  <code className="text-gray-300 text-xs">{ev}</code>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateKeyModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}
    </div>
  );
}
